// PoliciesIndexPage.tsx — /policies, the front door to all four documents.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Callout, MetaRow } from '@/ui';
import { POLICY_DOCUMENTS } from '@/core/data/policies';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 76ch;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Intro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Banner = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: color-mix(in srgb, ${({ theme }) => theme.color.safety} 14%, transparent);
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.55;
  color: ${({ theme }) => theme.color.safety};
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled(Panel).attrs({ as: Link })`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-decoration: none;
  transition: border-color 0.15s;

`;

const CardTitle = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const CardBlurb = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const CardMeta = styled(MetaRow)`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textFaint};
`;

export function PoliciesIndexPage() {
  const anyDraft = POLICY_DOCUMENTS.some((d) => d.status === 'draft');
  return (
    <Page>
      <Title>Terms &amp; Policies</Title>
      <Intro>
        What TruXSocial promises you, what we ask of you, and how moderation decisions get
        made. Each section has a plain-language summary next to the formal wording. The
        summary is there to help you understand the rule, and the formal wording is the rule.
      </Intro>

      {anyDraft && (
        <Banner role="note" data-testid="policies-draft-banner">
          <strong>These are drafts.</strong> They have not been reviewed by a lawyer, and some
          details are still marked for completion. They are published early because rules you
          cannot read are not rules you can follow. Tell us where they are wrong.
        </Banner>
      )}

      <Cards>
        {POLICY_DOCUMENTS.map((doc) => (
          <Card key={doc.id} to={`/policies/${doc.id}`} data-testid={`policy-card-${doc.id}`}>
            <CardTitle>{doc.title}</CardTitle>
            <CardBlurb>{doc.blurb}</CardBlurb>
            <CardMeta>
              <span>{doc.sections.length} sections</span>
              <span>updated {doc.updated}</span>
              {doc.status === 'draft' && <span>draft</span>}
            </CardMeta>
          </Card>
        ))}
      </Cards>

      {/* The documents promise a reporting route that needs no account. This page
          is public and the More hub is not, so without a link here that promise is
          only reachable by people who already have the account it says you don't
          need. Informational tone, not the danger hue — this box points somewhere,
          it does not warn. */}
      <Callout data-testid="policies-legal-floor-callout">
        <strong>Reporting content that is unlawful to host</strong> does not need an account, and
        you do not need to be the person affected:{' '}
        <Link to="/report-illegal">report illegal content</Link>. For everything the Council does
        judge, see <Link to="/help/reporting">Help with reporting</Link> and{' '}
        <Link to="/help/safety">Trust &amp; Safety</Link>.
      </Callout>
    </Page>
  );
}
